// src/components/Modal.tsx
import { useEffect, useRef } from "react";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Прежний проп, оставляем для совместимости */
  maxWidth?: string; // например "max-w-md"
  /** Новые, но опциональные */
  placement?: "center" | "bottom";
  height?: "auto" | "screen80"; // screen80 => max-h-[80vh] + прокрутка
  closeOnOverlay?: boolean;
  ariaLabel?: string;      // если нет заголовка
  labelledBy?: string;     // id заголовка внутри модалки
  showClose?: boolean;     // крестик
  onAfterClose?: () => void; // хук для цепочки анимаций
}

/* ===== Глобальный стек блокировок body ===== */
let __modalOpenCount = 0;
let __savedBodyStyles: { overflow: string; position: string; top: string; width: string } | null = null;
let __savedScrollY = 0;

function lockBodyScroll() {
  if (__modalOpenCount === 0) {
    __savedScrollY = window.scrollY || window.pageYOffset || 0;
    __savedBodyStyles = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${__savedScrollY}px`;
    document.body.style.width = "100%";
  }
  __modalOpenCount += 1;
}

function unlockBodyScroll() {
  __modalOpenCount = Math.max(0, __modalOpenCount - 1);
  if (__modalOpenCount === 0 && __savedBodyStyles) {
    const prev = __savedBodyStyles;
    document.body.style.overflow = prev.overflow;
    document.body.style.position = prev.position;
    document.body.style.top = prev.top;
    document.body.style.width = prev.width;
    window.scrollTo(0, __savedScrollY);
    __savedBodyStyles = null;
  }
}

export default function Modal({
  open,
  onClose,
  children,
  maxWidth = "max-w-md",
  placement = "center",
  height = "auto",
  closeOnOverlay = true,
  ariaLabel,
  labelledBy,
  showClose = true,
  onAfterClose,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const lastActiveRef = useRef<HTMLElement | null>(null);

  // Надёжная блокировка скролла body с общим счётчиком для нескольких модалок
  useEffect(() => {
    if (!open) return;

    lastActiveRef.current = document.activeElement as HTMLElement | null;

    lockBodyScroll();

    // Фокус внутрь модалки
    requestAnimationFrame(() => {
      panelRef.current?.focus();
    });

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", handleKey);

    return () => {
      document.removeEventListener("keydown", handleKey);

      // Возвращаем фокус туда, где он был до открытия
      lastActiveRef.current?.focus?.();

      // Разблокируем body (только когда закрыта последняя модалка)
      unlockBodyScroll();

      // Хук: безопасно выполнять авто-скролл после полного закрытия
      onAfterClose?.();
    };
  }, [open, onClose, onAfterClose]);

  if (!open) return null;

  const overlayClick = () => {
    if (closeOnOverlay) onClose();
  };

  const containerClass =
    placement === "bottom" ? "items-end" : "items-center";

  const panelBase =
    "bg-white w-full rounded-2xl shadow-xl relative outline-none focus:outline-none " +
    (placement === "bottom" ? "rounded-b-2xl rounded-t-3xl" : "");

  const widthClass = placement === "bottom" ? "" : maxWidth;
  const heightClass =
    height === "screen80" ? "max-h-[80vh] overflow-y-auto overscroll-contain" : "";

  // Простая анимация появления
  const animClass =
    placement === "bottom"
      ? "animate-[slideUp_200ms_ease-out]"
      : "animate-[zoomIn_160ms_ease-out]";

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center bg-black/40 backdrop-blur-[3px]"
      onClick={overlayClick}
      aria-hidden="true"
      style={{ pointerEvents: "auto" }}
    >
      <style>{`
        @keyframes zoomIn { from { opacity:.6; transform: scale(.98) } to { opacity:1; transform: scale(1) } }
        @keyframes slideUp { from { transform: translateY(8%); opacity:.9 } to { transform: translateY(0); opacity:1 } }
      `}</style>

      <div className={`flex w-full ${containerClass}`} aria-hidden="true">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
          aria-labelledby={labelledBy}
          tabIndex={-1}
          className={`${panelBase} ${widthClass} ${heightClass} ${animClass} mx-4 my-6`}
          onClick={(e) => e.stopPropagation()}
        >
          {showClose && (
            <button
              className="absolute top-4 right-4 h-8 w-8 rounded-full text-gray-500 hover:bg-gray-100 flex items-center justify-center"
              onClick={onClose}
              aria-label="Закрыть"
            >
              ×
            </button>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}
